import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { InputControl } from '@repo/ui/Input/InputControl';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { NavigationHeader } from '../NavigationHeader/NavigationHeader';
import { rawString, Text } from '../Language/Language';
import { useWorkflow } from '../../workflow/hooks';
import { StepHeader } from '../StepHeader/StepHeader';
import { NarvarFooter } from '../NarvarFooter/NarvarFooter';
import s from './GiftLogin.module.css';
import type { OrderTokenType } from '../../graphql/query/getRetailerSettings';
import { Icon } from '@repo/ui/Icon';
import { Button } from '@repo/ui/Button';
import { QUERIES, STORAGE_KEYS } from '../../utils/constants';
import { getOrderQueryFn } from '../../graphql/query/getOrder';
import { client } from '../../graphql/client';
import { safeSessionStorage } from '@repo/lib/storage';

const giftLoginSchema = z.object({
  ORDER_NUMBER: z.string().min(1, {
    message: rawString({
      langKey: 'pdx.auth.gift.order_number.required_error',
    }),
  }),
  ZIPCODE: z.string().min(1, {
    message: rawString({
      langKey: 'pdx.auth.gift.zipcode.required_error',
    }),
  }),
});

type GiftLoginSchemaType = z.infer<typeof giftLoginSchema>;

const fields: Array<keyof GiftLoginSchemaType> = ['ORDER_NUMBER', 'ZIPCODE'];

export const GiftLogin = () => {
  const router = useRouter();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    retailer,
    updateAuthStatus,
    setContextFromLogin,
    features,
    requestId,
  } = useWorkflow();
  const isGiftLoginEnabled = features?.isGiftLoginEnabled;
  const methods = useForm<GiftLoginSchemaType>({
    resolver: zodResolver(giftLoginSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { formState, register } = methods;
  const { isLoading, isError, refetch } = useQuery({
    enabled: false,
    retry: false,
    queryKey: [QUERIES.GET_ORDER],
    queryFn: () => {
      const values = methods.watch();
      const orderTokens = Object.entries(values).map(([key, value]) => ({
        key: key as OrderTokenType,
        value: value as string,
      }));
      return getOrderQueryFn({
        input: { orderTokens, isGift: true },
        skipEligibility: Boolean(requestId),
      });
    },
  });

  const redirectToItemSelection = useCallback(() => {
    navigate({
      to: '/$retailer/item-selection',
      search: (prev) => {
        const newSearch = { ...prev };
        delete newSearch.retailer;
        return newSearch;
      },
      params: { retailer: retailer ?? 's' },
    });
  }, [navigate, retailer]);

  const handleBack = () => {
    router.history.back();
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (isLoading) return;
    const response = await refetch();
    const order = response.data?.order;
    const submittedRequests = response.data?.submittedRequests;
    if (order) {
      const {
        authorization: { token },
        orderNumber,
      } = order;
      if (token) {
        client.setHeader('X-Authentication', token);
        safeSessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        safeSessionStorage.setItem(STORAGE_KEYS.ORDER_NUMBER, orderNumber);
        updateAuthStatus('authenticated');
        setContextFromLogin({
          order,
          loginContext: {
            orderNumber: data.ORDER_NUMBER,
            zipCode: data.ZIPCODE,
          },
          submittedRequests: submittedRequests ?? [],
        });
      }

      redirectToItemSelection();
    }
  });

  useEffect(() => {
    if (isAuthenticated || !isGiftLoginEnabled) {
      navigate({
        to: '/$retailer',
        params: { retailer: retailer ?? 's' },
        search: true,
      });
    }
  }, [isAuthenticated, isGiftLoginEnabled, retailer, navigate]);

  return (
    <div className={s.pageWrapper}>
      <NavigationHeader
        title={{
          langKey: 'pdx.auth.gift.page_header',
        }}
        onBack={handleBack}
      />
      <Box isContainer>
        <Flex className={s.main} direction="column" gap={['g-40', 'md:g-48']}>
          <Flex gap="g-24" padding="px-24" direction="column">
            <FormProvider {...methods}>
              <form className={s.form} onSubmit={handleSubmit}>
                <StepHeader
                  title={{ langKey: 'pdx.auth.gift.title' }}
                  subtitle={{ langKey: 'pdx.auth.gift.description' }}
                />
                <Flex direction="column" gap="g-8">
                  {fields.map((field) => (
                    <InputRoot
                      key={field}
                      variant={formState.errors[field] ? 'error' : 'default'}
                    >
                      <InputGroup>
                        <InputField>
                          <InputLabel htmlFor={field}>
                            {rawString({
                              langKey: `pdx.auth.gift.${field === 'ORDER_NUMBER' ? 'order_number' : 'zipcode'}`,
                            })}
                          </InputLabel>
                          <InputControl id={field} {...register(field)} />
                        </InputField>
                      </InputGroup>
                      {formState.errors[field] && (
                        <InputMessage>
                          <Icon name="warning" size="font" />
                          {formState.errors[field].message}
                        </InputMessage>
                      )}
                    </InputRoot>
                  ))}
                </Flex>
                <Button
                  isFullWidth={true}
                  isLoading={isLoading}
                  type="submit"
                  size="lg"
                >
                  <Text langKey="pdx.auth.gift.submit_button" />
                </Button>
                {isError && (
                  <div className={s.error} role="alert">
                    <Text langKey="pdx.auth.gift.submit_error_message" />
                  </div>
                )}
              </form>
            </FormProvider>
          </Flex>
        </Flex>
      </Box>
      <NarvarFooter />
    </div>
  );
};
